import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Switch,
  Textarea,
  Typography,
} from '@healthinal/ui';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getAppointmentDate, getAppointmentTimeFrom } from '../../../api/created/appointment.ts';
import {
  getGetLabOrdersByPatientQueryKey,
  useCreateTspotTest,
  useUpdateTSpotTest,
} from '../../../api/generated/lab-order-controller/lab-order-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AppointmentDto,
  CreateQuantiferonTestDtoInterpretation,
  CreateTspotTestDto,
  CreateTspotTestDtoInterpretation,
  CreateTuberculinTestDtoInterpretation,
  LabOrderDtoType,
} from '../../../api/generated/model';
import { queryClient } from '../../../api/queryClient.ts';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { getDefaultValueForDateInput, getDefaultValueForLocalTimeInput } from '../../../utils/date.ts';
import { blobToBase64 } from '../../../utils/file.ts';
import { toLabOrderSamplingContact } from '../../../utils/labOrder.ts';
import { getStringOrUndefined } from '../../../utils/string.ts';
import { DeleteLabOrderButton } from './DeleteLabOrderButton.tsx';
import { LabOrderDetailsForm } from './LabOrderDetailsForm.tsx';
import { ResultInterpretationButtonGroup } from './ResultInterpretationButtonGroup.tsx';

interface FormValues {
  samplingDate: string;
  samplingTime: string;
  samplingAddress: AddressDto | null;
  samplingContact: AddressContactDto | null;
  esat6Result: string;
  cfp10Result: string;
  interpretation: CreateQuantiferonTestDtoInterpretation | null;
  file: File | null;
  notes: string;
  isTodo: boolean;
}

export function CreateTspotResultForm({
  appointment,
  patientId,
  onClose,
  currentResult,
  currentResultId,
}: {
  appointment?: AppointmentDto | null;
  patientId: string;
  onClose: () => void;
  currentResult?: CreateTspotTestDto;
  currentResultId?: string;
}) {
  const isEditing = !!currentResultId;

  const formMethods = useForm<FormValues>({
    defaultValues: getDefaultValues(appointment, currentResult),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty: isFormDirty },
  } = formMethods;

  const isSamplingAddressDisabled = !!currentResult?.samplingAddress;
  const isSamplingContactDisabled = !!currentResult?.samplingContact;
  const isFileInputDisabled = !!currentResult?.file;
  const isEsat6ResultRequired = !!currentResult?.esat6Result;
  const isCfp10ResultRequired = !!currentResult?.cfp10Result;
  const isInterpretationRequired =
    !!currentResult?.interpretation && currentResult.interpretation !== CreateTuberculinTestDtoInterpretation.PENDING;

  const createTspotTestMutation = useCreateTspotTest({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabOrdersByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim speichern des Testresultates ist ein Fehler aufgetreten'),
    },
  });

  const updateTspotTestMutation = useUpdateTSpotTest({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabOrdersByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim Aktualisieren des Testresultats ist ein Fehler aufgetreten'),
    },
  });

  const handleOnSubmit = async (data: FormValues) => {
    const createTspotTestDto: CreateTspotTestDto = {
      appointmentId: appointment?.id,
      cfp10Result: getStringOrUndefined(data.cfp10Result),
      esat6Result: getStringOrUndefined(data.esat6Result),
      file: data.file
        ? {
            fileName: data.file.name,
            file: await blobToBase64(data.file),
          }
        : undefined,
      interpretation: data.interpretation ?? CreateTuberculinTestDtoInterpretation.PENDING,
      notes: getStringOrUndefined(data.notes),
      isTodo: data.isTodo,
      patientId: patientId,
      samplingDate: data.samplingDate,
      samplingTime: getStringOrUndefined(data.samplingTime),
      samplingAddress: data.samplingAddress ?? undefined,
      samplingContact: toLabOrderSamplingContact(data.samplingContact),
    };
    if (isEditing) {
      updateTspotTestMutation.mutate({ id: currentResultId, data: createTspotTestDto });
    } else {
      createTspotTestMutation.mutate({ data: createTspotTestDto });
    }
  };

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <Stack gap={2}>
          <Typography level={'title-lg'}>T-Spot</Typography>
          <LabOrderDetailsForm
            samplingDate={'samplingDate'}
            samplingTime={'samplingTime'}
            samplingAddress={'samplingAddress'}
            samplingContact={'samplingContact'}
            file={'file'}
            isSamplingAddressDisabled={isSamplingAddressDisabled}
            isSamplingContactDisabled={isSamplingContactDisabled}
            fileInputPlaceHolder={currentResult?.file?.fileName ?? 'Laborbefund'}
            fileInputName={'PDF Laborbefund'}
            fileInputMimeType={'application/pdf'}
            isFileInputDisabled={isFileInputDisabled}
            isEditing={isEditing}
          />
          <Divider inset={'context'} />
          <Typography level={'title-lg'}>Resultat</Typography>
          <Stack direction={'row'} gap={2}>
            <FormControl sx={{ flex: 1 }} error={!!errors.esat6Result}>
              <FormLabel>
                <Typography>Laborwerte T Spot ESAT 6 {isEsat6ResultRequired && '*'}</Typography>
              </FormLabel>
              <Input {...register('esat6Result', { required: isEsat6ResultRequired })} />
              {errors.esat6Result && <FormHelperText>Laborwerte T Spot ESAT 6 muss ausgefüllt werden</FormHelperText>}
            </FormControl>
            <FormControl sx={{ flex: 1 }} error={!!errors.cfp10Result}>
              <FormLabel>
                <Typography>Laborwerte T Spot CFP 10 {isCfp10ResultRequired && '*'}</Typography>
              </FormLabel>
              <Input {...register('cfp10Result', { required: isCfp10ResultRequired })} />
              {errors.cfp10Result && <FormHelperText>Laborwerte T Spot CFP 10 muss ausgefüllt werden</FormHelperText>}
            </FormControl>
          </Stack>
          <Stack direction={'row'} gap={2}>
            <FormControl sx={{ flex: 1 }} error={!!errors.interpretation}>
              <FormLabel>
                <Typography>Resultat {isInterpretationRequired && '*'}</Typography>
              </FormLabel>
              <Controller
                name={'interpretation'}
                rules={{ required: isInterpretationRequired }}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ResultInterpretationButtonGroup
                    value={value}
                    onChange={onChange}
                    resultOptions={[
                      CreateQuantiferonTestDtoInterpretation.POSITIVE,
                      CreateQuantiferonTestDtoInterpretation.NEGATIVE,
                      CreateQuantiferonTestDtoInterpretation.INDETERMINATE,
                    ]}
                  />
                )}
              />
              {errors.interpretation && <FormHelperText>Resultat muss angegeben werden</FormHelperText>}
            </FormControl>
          </Stack>
          <Divider inset={'context'} />
          <Typography level={'title-lg'}>Notiz</Typography>
          <Textarea {...register('notes')} minRows={2} maxRows={4} />
          {!isEditing && (
            <Controller
              name="isTodo"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value}
                  onChange={onChange}
                  endDecorator={'Handlung erforderlich (ist ToDo)'}
                  sx={{ paddingTop: '1rem', alignSelf: 'flex-start' }}
                />
              )}
            />
          )}
          <FormActionButtons
            onAbortClick={handleOnClose}
            disableSubmit={!isFormDirty}
            isLoading={createTspotTestMutation.isPending}>
            {currentResultId && (
              <DeleteLabOrderButton
                orderId={currentResultId}
                patientId={patientId}
                appointmentId={currentResult?.appointmentId}
                orderType={LabOrderDtoType.BLOOD_TSPOT}
              />
            )}
          </FormActionButtons>
        </Stack>
      </form>
    </FormProvider>
  );
}

function getDefaultValues(appointment?: AppointmentDto | null, currentResult?: CreateTspotTestDto): FormValues {
  return {
    samplingDate: getDefaultValueForDateInput(
      currentResult?.samplingDate,
      getAppointmentDate(appointment) ?? undefined,
    ),
    samplingTime: currentResult?.samplingTime
      ? getDefaultValueForLocalTimeInput(currentResult.samplingTime)
      : (getAppointmentTimeFrom(appointment) ?? ''),
    samplingAddress: currentResult?.samplingAddress ?? null,
    samplingContact: currentResult?.samplingContact ?? null,
    esat6Result: currentResult?.esat6Result ?? '',
    cfp10Result: currentResult?.cfp10Result ?? '',
    interpretation:
      currentResult?.interpretation === CreateTspotTestDtoInterpretation.PENDING
        ? null
        : (currentResult?.interpretation ?? null),
    file: null,
    notes: currentResult?.notes ?? '',
    isTodo: currentResult?.isTodo ?? false,
  };
}
